/**
 * Intent-based prefetching for internal links.
 *
 * Two signals are used, cheapest first:
 *  1. Viewport — links that scroll into view are queued for a low-priority
 *     preload during idle time (max 4 concurrent, capped per page).
 *  2. Intent — pointerenter / touchstart / focus fire an immediate preload,
 *     which typically buys 100-300ms before the click actually lands.
 *
 * Everything is skipped on Save-Data or 2g/slow-2g connections so we never
 * spend a constrained user's bandwidth on a page they may not visit.
 */

type Preloader = (href: string) => void;

const preloaded = new Set<string>();
let inFlight = 0;
const queue: string[] = [];
const MAX_CONCURRENT = 3;
const MAX_PER_SESSION = 60;

export function shouldPrefetch(): boolean {
  if (typeof navigator === "undefined") return false;
  const c = (
    navigator as unknown as {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;
  if (c?.saveData) return false;
  if (c?.effectiveType && /2g/.test(c.effectiveType)) return false;
  return true;
}

/** Normalizes an anchor into a same-origin path, or null if not prefetchable. */
export function internalPath(a: HTMLAnchorElement): string | null {
  if (a.target && a.target !== "_self") return null;
  if (a.hasAttribute("download")) return null;
  if (a.dataset.noPrefetch !== undefined) return null;
  const href = a.getAttribute("href");
  if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:"))
    return null;
  let url: URL;
  try {
    url = new URL(a.href, window.location.href);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  // Skip assets, API routes and the admin area.
  if (/\.[a-z0-9]{2,5}$/i.test(url.pathname)) return null;
  if (url.pathname.startsWith("/api/") || url.pathname.startsWith("/admin")) return null;
  if (url.pathname === window.location.pathname) return null;
  return url.pathname + url.search;
}

function drain(preload: Preloader) {
  while (inFlight < MAX_CONCURRENT && queue.length > 0) {
    const href = queue.shift()!;
    if (preloaded.has(href)) continue;
    preloaded.add(href);
    inFlight++;
    try {
      preload(href);
    } finally {
      // Router preloads resolve asynchronously; release the slot on the next
      // idle tick rather than tracking each promise, which keeps this cheap.
      idle(() => {
        inFlight--;
        if (queue.length) drain(preload);
      });
    }
  }
}

function idle(cb: () => void) {
  const ric = (window as unknown as { requestIdleCallback?: (c: () => void, o?: unknown) => void })
    .requestIdleCallback;
  if (ric) ric(cb, { timeout: 800 });
  else setTimeout(cb, 120);
}

export function enqueuePrefetch(href: string, preload: Preloader, immediate = false) {
  if (!shouldPrefetch()) return;
  if (preloaded.has(href) || preloaded.size >= MAX_PER_SESSION) return;
  if (immediate) {
    preloaded.add(href);
    preload(href);
    return;
  }
  queue.push(href);
  idle(() => drain(preload));
}

/**
 * Wires viewport + intent listeners on the document. Returns a cleanup fn.
 */
export function observeLinks(preload: Preloader): () => void {
  if (typeof window === "undefined" || !shouldPrefetch()) return () => {};

  const seen = new WeakSet<HTMLAnchorElement>();

  const io =
    "IntersectionObserver" in window
      ? new IntersectionObserver(
          (entries) => {
            for (const e of entries) {
              if (!e.isIntersecting) continue;
              const a = e.target as HTMLAnchorElement;
              io?.unobserve(a);
              const path = internalPath(a);
              if (path) enqueuePrefetch(path, preload);
            }
          },
          { rootMargin: "200px" },
        )
      : null;

  const scan = () => {
    const anchors = document.querySelectorAll<HTMLAnchorElement>("a[href]");
    anchors.forEach((a) => {
      if (seen.has(a)) return;
      seen.add(a);
      if (internalPath(a)) io?.observe(a);
    });
  };

  const onIntent = (event: Event) => {
    const target = event.target as Element | null;
    const a = target?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a) return;
    const path = internalPath(a);
    if (path) enqueuePrefetch(path, preload, true);
  };

  idle(scan);
  const mo = new MutationObserver(() => idle(scan));
  mo.observe(document.body, { childList: true, subtree: true });

  document.addEventListener("pointerenter", onIntent, { capture: true, passive: true });
  document.addEventListener("touchstart", onIntent, { capture: true, passive: true });
  document.addEventListener("focusin", onIntent, { passive: true });

  return () => {
    io?.disconnect();
    mo.disconnect();
    document.removeEventListener("pointerenter", onIntent, { capture: true });
    document.removeEventListener("touchstart", onIntent, { capture: true });
    document.removeEventListener("focusin", onIntent);
  };
}
